import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Shirt, CheckCircle2 } from "lucide-react";

function MerchWaitlist() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", interests: "", notes: "", preferred_language: lang });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/merch-waitlist", form);
      setDone(true);
      toast.success("You're on the waitlist");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to sign up");
    } finally { setSubmitting(false); }
  };
  return (
    <section data-testid="merch-waitlist" className="mt-24 grid lg:grid-cols-12 gap-10 items-start">
      <div className="lg:col-span-5">
        <p className="eyebrow"><Shirt className="inline h-3.5 w-3.5 mr-1" strokeWidth={1.5}/> Merch waitlist</p>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl text-forest-900 leading-[1.1]">Be first when the first drop lands.</h2>
        <p className="mt-6 text-forest-700 leading-relaxed">
          A small, considered Sunbraider merchandise line will follow later — only things I actually want to wear and use. Add your name so I can quietly let you know when it arrives.
        </p>
        <p className="mt-4 text-xs text-forest-700/80">
          Signed-in supporters earn <span className="font-semibold text-wheat-700">+30 points</span> for joining the waitlist.
        </p>
      </div>
      <div className="lg:col-span-7">
        {done ? (
          <div className="card-warm flex items-start gap-4" data-testid="merch-success">
            <CheckCircle2 className="h-8 w-8 text-wheat-600 shrink-0" strokeWidth={1.25}/>
            <div>
              <h3 className="font-serif text-2xl text-forest-900">You&apos;re on the list.</h3>
              <p className="mt-2 text-sm text-forest-700">Thank you. I&apos;ll write you personally when the first pieces are ready.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="card-warm grid md:grid-cols-2 gap-5" data-testid="merch-form">
            <div>
              <label className="field-label">Name</label>
              <input required data-testid="merch-name" className="field-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}/>
            </div>
            <div>
              <label className="field-label">Email</label>
              <input required type="email" data-testid="merch-email" className="field-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}/>
            </div>
            <div className="md:col-span-2">
              <label className="field-label">What would you love (optional)</label>
              <input placeholder="sweatshirt, candle, tote, journal…" data-testid="merch-interests" className="field-input" value={form.interests} onChange={(e) => setForm({ ...form, interests: e.target.value })}/>
            </div>
            <div className="md:col-span-2">
              <label className="field-label">Notes (optional)</label>
              <textarea rows={3} data-testid="merch-notes" className="field-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/>
            </div>
            <div className="md:col-span-2">
              <button className="btn-primary" disabled={submitting} data-testid="merch-submit">
                {submitting ? t("common.loading") : "Join the waitlist"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

export default function FutureServices() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/future-offerings").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="future-services-page" className="container-refined py-16 md:py-24">
      <div className="max-w-2xl">
        <p className="eyebrow">{t("future.eyebrow")}</p>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[1.05] text-forest-900">{t("future.title")}</h1>
        <p className="mt-6 text-forest-700 leading-relaxed">{t("future.lede")}</p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="future-services-grid">
        {loading && <p className="text-forest-700">{t("common.loading")}</p>}
        {!loading && items.map((it) => (
          <div key={it.id} className="card-warm relative" data-testid={`future-item-${it.category}`}>
            <span className="absolute top-6 right-6 text-[10px] uppercase tracking-widest text-wheat-700 bg-wheat-50 rounded-full px-3 py-1 border border-wheat-500/30">
              {t("future.coming_later")}
            </span>
            <Sparkles className="h-5 w-5 text-forest-800" strokeWidth={1.25} />
            <h3 className="mt-4 font-serif text-2xl text-forest-900">{it.title}</h3>
            <p className="mt-3 text-sm text-forest-700 leading-relaxed">{it.description}</p>
          </div>
        ))}
      </div>

      <MerchWaitlist />
    </div>
  );
}
